/**
 * Lightweight Headroom - A minimal header hide/show on scroll
 * TypeScript version with full type safety
 * Size: ~1KB minified (after compilation)
 */

interface HeadroomClasses {
  initial: string;
  pinned: string;
  unpinned: string;
  top: string;
  notTop: string;
}

interface HeadroomOptions {
  offset?: number;
  tolerance?: number;
  classes?: Partial<HeadroomClasses>;
}

interface HeadroomState {
  lastScrollY: number;
  currentScrollY: number;
  isScrolling: boolean;
  isPinned: boolean;
  isTop: boolean;
}

type ScrollDirection = 'up' | 'down';

class HeadroomLite {
  private element: HTMLElement;
  // @ts-expect-error
  private options: Required<HeadroomOptions>;
  // @ts-expect-error
  private state: HeadroomState;
  // @ts-expect-error
  private scrollHandler: (event: Event) => void;
  private rafId: number | null = null;

  constructor(element: string | HTMLElement, options: HeadroomOptions = {}) {
    this.element = typeof element === 'string' ? (document.querySelector(element) as HTMLElement) : element;

    if (!this.element) {
      console.warn('HeadroomLite: Element not found');
      return;
    }

    // Default options with type safety
    const defaultClasses: HeadroomClasses = {
      initial: 'headroom',
      pinned: 'headroom--pinned',
      unpinned: 'headroom--unpinned',
      top: 'headroom--top',
      notTop: 'headroom--not-top',
    };

    this.options = {
      offset: options.offset ?? 0,
      tolerance: options.tolerance ?? 5,
      classes: { ...defaultClasses, ...options.classes },
    };

    // Initialize state
    this.state = {
      lastScrollY: 0,
      currentScrollY: 0,
      isScrolling: false,
      isPinned: true,
      isTop: true,
    };

    // Bind methods to maintain context
    this.scrollHandler = this.createScrollHandler();

    this.init();
  }

  private init(): void {
    // Add initial classes
    this.element.classList.add(this.options.classes.initial);
    this.element.classList.add(this.options.classes.top);
    this.element.classList.add(this.options.classes.pinned);

    // Bind scroll event
    this.bindEvents();

    // Initial check
    this.update();
  }

  private bindEvents(): void {
    // Use passive listener for better performance
    window.addEventListener('scroll', this.scrollHandler, { passive: true });
  }

  private createScrollHandler(): (event: Event) => void {
    let ticking = false;

    return (): void => {
      this.state.currentScrollY = Math.max(0, window.pageYOffset);

      if (!ticking) {
        this.rafId = requestAnimationFrame(() => {
          this.update();
          ticking = false;
        });
        ticking = true;
      }
    };
  }

  private update(): void {
    const scrollY = this.state.currentScrollY;
    const scrollDirection: ScrollDirection = scrollY > this.state.lastScrollY ? 'down' : 'up';
    const distanceScrolled = Math.abs(scrollY - this.state.lastScrollY);

    // Check if at top
    const isTop = scrollY <= this.options.offset;
    if (isTop !== this.state.isTop) {
      this.state.isTop = isTop;
      this.toggleClass(this.options.classes.top, isTop);
      this.toggleClass(this.options.classes.notTop, !isTop);
    }

    // Only proceed if scrolled enough to matter
    if (distanceScrolled <= this.options.tolerance) {
      return;
    }

    // Determine pin state
    const shouldPin = scrollDirection === 'up' || isTop;

    if (shouldPin !== this.state.isPinned) {
      this.state.isPinned = shouldPin;
      this.toggleClass(this.options.classes.pinned, shouldPin);
      this.toggleClass(this.options.classes.unpinned, !shouldPin);
    }

    this.state.lastScrollY = scrollY;
  }

  private toggleClass(className: string, add: boolean): void {
    this.element.classList.toggle(className, add);
  }

  // Public methods
  public pin(): void {
    this.state.isPinned = true;
    this.element.classList.add(this.options.classes.pinned);
    this.element.classList.remove(this.options.classes.unpinned);
  }

  public unpin(): void {
    this.state.isPinned = false;
    this.element.classList.add(this.options.classes.unpinned);
    this.element.classList.remove(this.options.classes.pinned);
  }

  public destroy(): void {
    // Cancel any pending animation frame
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    // Remove event listeners
    window.removeEventListener('scroll', this.scrollHandler);

    // Remove all classes
    const classes = Object.values(this.options.classes);
    this.element.classList.remove(...classes);
  }

  // Getters for current state
  public get isPinned(): boolean {
    return this.state.isPinned;
  }

  public get isTop(): boolean {
    return this.state.isTop;
  }

  public get currentScroll(): number {
    return this.state.currentScrollY;
  }
}

// Auto-initialize function with type safety
function autoInitialize(): void {
  document.querySelectorAll<HTMLElement>('[data-headroom]').forEach((element) => {
    try {
      const optionsStr = element.dataset.headroomOptions;
      const options: HeadroomOptions = optionsStr ? JSON.parse(optionsStr) : {};
      new HeadroomLite(element, options);
    } catch (error) {
      console.warn('HeadroomLite: Failed to parse options for element', element, error);
    }
  });
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', autoInitialize);
} else {
  autoInitialize();
}

// Export for manual initialization
declare global {
  interface Window {
    HeadroomLite: typeof HeadroomLite;
  }
}

window.HeadroomLite = HeadroomLite;

export default HeadroomLite;
export type { HeadroomClasses, HeadroomOptions };
